import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Instagram, Linkedin, Send } from "lucide-react";
import { Seo } from "@/components/Seo";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const t = useT();
  const REASONS = [t.reason_1, t.reason_2, t.reason_3, t.reason_4, t.reason_5, t.reason_6];
  const [form, setForm] = useState({ name: "", company: "", email: "", reason: REASONS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error(t.contact_err_fill); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact/messages`, form);
      toast.success(t.contact_success);
      setForm({ name: "", company: "", email: "", reason: REASONS[0], message: "" });
    } catch { toast.error(t.contact_fail); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Seo title={t.seo_contact_title} description={t.seo_contact_desc} />
      <section className="pt-12 pb-8 carbon-tex" data-testid="contact-hero">
        <div className="container-ar">
          <div className="eyebrow mb-4">{t.nav_contact}</div>
          <h1 className="h1-hero text-5xl md:text-7xl mb-4">{t.contact_h1_a} <span className="text-[#e77300]">{t.contact_h1_b}</span></h1>
          <p className="text-white/70 max-w-2xl">{t.contact_intro}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-ar grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-4">
            <div className="tele-card p-6">
              <Mail className="w-5 h-5 text-[#e77300] mb-3" />
              <div className="eyebrow">{t.lb_email}</div>
              <a href="mailto:Partnerships@alightracing.com" className="font-display uppercase text-lg break-all hover:text-[#e77300]" data-testid="contact-email-link">Partnerships@alightracing.com</a>
            </div>
            <div className="tele-card p-6">
              <MapPin className="w-5 h-5 text-[#e77300] mb-3" />
              <div className="eyebrow">{t.lb_location}</div>
              <div className="font-display uppercase text-lg">{t.footer_location}</div>
              <div className="text-xs text-[#8ba3cc] mt-1">Colégio Universitário Londrina</div>
            </div>
            <div className="tele-card p-6">
              <div className="eyebrow mb-3">{t.lb_follow}</div>
              <div className="flex items-center gap-3">
                <a href="#" className="w-11 h-11 border border-[#005fca]/40 flex items-center justify-center hover:border-[#e77300] hover:text-[#e77300]" data-testid="contact-instagram"><Instagram className="w-5 h-5" /></a>
                <a href="#" className="w-11 h-11 border border-[#005fca]/40 flex items-center justify-center hover:border-[#e77300] hover:text-[#e77300]" data-testid="contact-linkedin"><Linkedin className="w-5 h-5" /></a>
              </div>
              <p className="text-xs text-[#8ba3cc] mt-4">{t.follow_note}</p>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-8 tele-card p-8 space-y-5" data-testid="contact-form">
            <div className="eyebrow">{t.lb_send}</div>
            <h2 className="h2-section text-3xl md:text-4xl">{t.contact_h}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="eyebrow block mb-2">{t.fld_name} *</label><input className="ar-input" value={form.name} onChange={set("name")} data-testid="contact-name" required /></div>
              <div><label className="eyebrow block mb-2">{t.fld_company}</label><input className="ar-input" value={form.company} onChange={set("company")} data-testid="contact-company" /></div>
              <div><label className="eyebrow block mb-2">{t.fld_email} *</label><input type="email" className="ar-input" value={form.email} onChange={set("email")} data-testid="contact-email" required /></div>
              <div>
                <label className="eyebrow block mb-2">{t.fld_reason}</label>
                <select className="ar-input" value={form.reason} onChange={set("reason")} data-testid="contact-reason">
                  {REASONS.map((r) => <option key={r} value={r} className="bg-[#020815]">{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-2">{t.fld_message} *</label>
              <textarea className="ar-input" rows={6} value={form.message} onChange={set("message")} data-testid="contact-message" required />
            </div>
            <button type="submit" disabled={loading} className="btn-racing primary" data-testid="contact-submit">
              <span>{loading ? t.contact_sending : t.btn_send} <Send className="w-4 h-4" /></span>
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
